import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Input, Button, Tag as AntTag, Typography, message } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import pagesService from '../../services/pagesService';
import { useCommon } from '../../context/CommonContext';
const { Title, Text } = Typography;
const { Search } = Input;

const PageList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} pages`,
  });
  const [filters, setFilters] = useState({ searchText: '' });

  useEffect(() => {
    setPageTitle("Pages");
  }, [setPageTitle]);

  const getPages = async () => {
    try {
      setLoading(true);
      const res = await pagesService.list({
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.searchText,
        status: 'all',
      });

      if (res?.data?.status) {
        const data = res.data.data || [];
        const meta = res.data.meta || {};

        setItems(Array.isArray(data) ? data : []);
        setPagination((prev) => ({
          ...prev,
          total: meta.totalEntries || prev.total,
          current: meta.currentPage || prev.current,
          pageSize: meta.pageSize || prev.pageSize,
        }));
      } else {
        setItems([]);
      }
    } catch (e) {
      console.error(e);
      message.error('Failed to fetch pages');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.searchText]);

  const handleTableChange = ({ current, pageSize }) => {
    setPagination((prev) => ({ ...prev, current, pageSize }));
  };

  const handleSearch = (value) => {
    setFilters((f) => ({ ...f, searchText: value }));
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'page_type',
      key: 'page_type',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (v) => <AntTag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</AntTag>,
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      render: (t, r) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/pages/edit/${r.page_id || r.id}`)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <Title level={4} style={{ margin: 0 }}>Pages ({pagination.total})</Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Search
            placeholder="Search pages..."
            allowClear
            value={filters.searchText}
            onChange={(e) => {
              const value = e.target.value;
              setFilters((f) => ({ ...f, searchText: value }));
              setPagination((p) => ({ ...p, current: 1 }));
            }}
            onSearch={handleSearch}
            style={{ width: 320, maxWidth: '40vw' }}
            enterButton={<SearchOutlined />}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/pages/add/new')}>Add Page</Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={items}
        rowKey={(r) => r.page_id || r.id}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        size="middle"
      />
    </Card>
  );
};

export default PageList;
